import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './PokemonCard.css';
import { formatFormLabel } from '../utils/forms';

function getBaseName(pokemonName) {
  return pokemonName
    .replace(/\s*\(Mega\)/, '')
    .replace(/\s*\(Shadow\)/, '')
    .trim();
}

function PokemonCard({ pokemon, onClick, isSelected }) {
  const navigate = useNavigate();
  // Get the icon filename from the pokemon data
  const iconUrl = pokemon.icon_filename 
    ? `/pokemon_icons/${pokemon.icon_filename}`
    : null;

  const baseName = getBaseName(pokemon.pokemon_name);
  const profileUrl = `/pokemon/${encodeURIComponent(baseName)}`;
  const cardId = `tier-card-${pokemon.pokemon_name}-${pokemon.form}`;

  const isShadow =
    pokemon.form === 'Shadow' ||
    (pokemon.pokemon_name && /\(Shadow\)/.test(pokemon.pokemon_name));

  const displayName = useMemo(() => {
    if (!pokemon) return '';
    const name = pokemon.pokemon_name || '';
    // If the name already includes a parenthetical form (e.g., "Mewtwo (Shadow)"),
    // use it as-is so we don't duplicate form labels.
    if (/\([^)]*\)/.test(name)) {
      return name;
    }
    if (pokemon.form && pokemon.form !== 'Normal') {
      return `${name} (${formatFormLabel(pokemon.form)})`;
    }
    return name;
  }, [pokemon]);

  const handleCardClick = () => {
    navigate(profileUrl);
  };

  const handleAddToParty = (e) => {
    e.stopPropagation();
    if (onClick) onClick();
  };

  return (
    <div 
      id={cardId}
      className={`pokemon-card ${isSelected ? 'selected' : ''} ${
        isShadow ? 'shadow' : ''
      } clickable`}
      onClick={handleCardClick}
    >
      {iconUrl && (
        <div className={`pokemon-icon-wrap ${isShadow ? 'pokemon-icon-wrap--shadow' : ''}`}>
          {isShadow && (
            <span className="pokemon-icon-bubble" aria-hidden="true" />
          )}
          <img 
            src={iconUrl} 
            alt={pokemon.pokemon_name}
            className="pokemon-icon"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="pokemon-content">
        <div className="pokemon-header">
          <div className="pokemon-header-left">
            <span className="rank">#{pokemon.rank}</span>
            <span className="pokemon-name">
              {displayName}
            </span>
          </div>
          <div className="pokemon-header-right">
            <div className="dps-stat">
              <span className="dps-label">L50 DPS:</span>
              <span className="dps-value">{Number(pokemon.combined_effective_dps).toFixed(1)}</span>
            </div>
            {onClick && (
              <button
                className="add-to-party-btn"
                onClick={handleAddToParty}
                title="Add to party"
              >
                + Party
              </button>
            )}
          </div>
        </div>
        
        <div className="moves">
          <div className="move fast-move">
            <div className="move-content">
              <span className="move-label">Fast:</span> {pokemon.fast_move}
              {pokemon.elite_fast_tm && (
                <span className="elite-badge elite-fast">
                  Elite TM
                  {pokemon.dps_without_elite_fast && 
                    ` (${Number(pokemon.dps_without_elite_fast).toFixed(1)} L50 DPS without)`}
                </span>
              )}
            </div>
          </div>
          <div className="move charged-move">
            <div className="move-content">
              <span className="move-label">Charged:</span> {pokemon.charged_move}
              {pokemon.elite_charged_tm && (
                <span className="elite-badge elite-charged">
                  Elite TM
                  {pokemon.dps_without_elite_charged && 
                    ` (${Number(pokemon.dps_without_elite_charged).toFixed(1)} L50 DPS without)`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PokemonCard;
